import { ReactElement, useCallback, useEffect } from 'react';
import { fetchProviders, fetchUsers } from '@/features/Manifests/components/ManifestFilters/utils';

import { AirportGroupDropdown } from '@/components/AirportDropdown';
import Button from '@/common/Button';
import ConfirmationButton from '@/components/ConfirmationButton';
import { DATE_FE_FORMAT } from '@/constants';
import DateInput from '@/common/DateInput';
import FormButton from '@/components/FormButton';
import ManifestSettingsModal from '@/features/Manifests/components/ManifestSettingsModal';
import { ManifestsTypes } from '@/features/Manifests/types';
import QueryFilters from '@/components/QueryFilters';
import QuerySelect from '@/common/QuerySelect';
import deleteManifestImportBulk from '@/features/Manifests/api/services/deleteManifestImportBulk';
import { handleError } from '@/utils';
import { useManifestsState } from '@/features/Manifests';

const ManifestFilters = (): ReactElement => {
  const [
    { from, to, createdBy, airportCode, providerId, fetch, selected, toggleModal, onReset, manifestSettings, rememberFilters },
    setState,
  ] = useManifestsState(({ state, setState }) => [
    {
      from: state.filters.from,
      to: state.filters.to,
      createdBy: state.filters.createdBy,
      airportCode: state.filters.airportCode,
      providerId: state.filters.providerId,
      fetch: state.fetch,
      selected: state.selected,
      toggleModal:
        (name: keyof ManifestsTypes.Modals, show?: boolean): (() => void) =>
        (): void => {
          setState((current) => ({
            ...current,
            modals: {
              ...current.modals,
              [name]: { ...current.modals[name], show: show ?? !current.modals[name]?.show },
            },
          }));
        },
      onReset: state.onReset,
      manifestSettings: state.modals.manifestSettings,
      rememberFilters: state.settings.rememberFilters,
    },
    setState,
  ]);

  const handleChange =
    (key: keyof ManifestsTypes.Filters) =>
    (value): void => {
      let newValue = value;
      setState((current) => {
        switch (key) {
          case 'airportCode':
            return { ...current, filters: { ...current.filters, [key]: typeof newValue === 'string' ? newValue.split(',') : newValue } };
          case 'from':
            return {
              ...current,
              filters: {
                ...current.filters,
                [key]: newValue,
                to: typeof newValue === 'string' && current.filters.to < newValue ? newValue : current.filters.to,
              },
            };
          case 'to':
            return {
              ...current,
              filters: {
                ...current.filters,
                [key]: newValue,
                from: typeof newValue === 'string' && current.filters.from > newValue ? newValue : current.filters.from,
              },
            };
          default:
            return { ...current, filters: { ...current.filters, [key]: newValue !== current.filters[key] ? newValue : '' } };
        }
      });
    };

  const getUsers = useCallback(async (): Promise<ReactElement[]> => fetchUsers(airportCode || []), [airportCode]);

  const deleteImports = async (): Promise<void> => {
    try {
      await deleteManifestImportBulk(selected);
      await fetch();
      setState((current) => ({ ...current, selected: [] }));
    } catch (err) {
      handleError(err);
    }
  };

  useEffect(() => {
    (rememberFilters ? fetch : onReset)();
    /* ONLY ON MOUNT
      Ignore other dependencies so this only fires on mount.
    */ // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <QueryFilters className="ManifestFilters justify-content-between">
        <div className="d-flex gap-2">
          <QueryFilters.Control>
            <DateInput name="from" value={from} onChange={(value: string): void => handleChange('from')(value)} format={DATE_FE_FORMAT} />
            <DateInput name="to" value={to} onChange={(value: string): void => handleChange('to')(value)} format={DATE_FE_FORMAT} />
          </QueryFilters.Control>
          <QueryFilters.Input>
            <QuerySelect
              name="createdBy"
              query={getUsers}
              value={createdBy}
              onChange={handleChange('createdBy')}
              placeholder="Imported By"
            />
          </QueryFilters.Input>
          <QueryFilters.Input>
            <AirportGroupDropdown
              value={airportCode}
              onChange={handleChange('airportCode')}
              options={{
                showClearButton: false,
                locale: {
                  'Select...': 'Airport',
                },
              }}
            />
            {/* <QuerySelect
              name="airportCode"
              query={fetchAirportGroups}
              value={airportCode}
              onChange={handleChange('airportCode')}
              placeholder="Airport"
            /> */}
          </QueryFilters.Input>
          <QueryFilters.Input>
            <QuerySelect
              name="providerId"
              onChange={handleChange('providerId')}
              value={providerId}
              query={fetchProviders}
              placeholder="Airline"
            />
          </QueryFilters.Input>
          <QueryFilters.Control>
            <Button name="SUBMIT_SEARCH" variant="success" className="border-white" onClick={fetch}>
              GO
            </Button>
            <Button name="RESET_SEARCH" variant="outline-light" onClick={onReset}>
              Reset
            </Button>
          </QueryFilters.Control>
        </div>
        <div className="d-flex gap-2">
          <QueryFilters.Control>
            <FormButton
              name="IMPORT_SCHEDULES"
              icon={<i className="sv sv-download2 fs-4" />}
              variant="outline-light"
              onClick={toggleModal('manifestUpload', true)}
            >
              Import Schedules
            </FormButton>
          </QueryFilters.Control>
          <QueryFilters.Control>
            <ConfirmationButton
              className="delete-button"
              variant="outline-light"
              disabled={!selected.length}
              onConfirm={deleteImports}
              options={{
                confirmation: {
                  Header: {
                    title: 'Confirm Delete',
                  },
                  Body: {
                    message:
                      selected?.length > 1
                        ? `Are you sure you want to delete these ${selected.length} manifests?`
                        : 'Are you sure you want to delete this manifest?',
                  },
                  Footer: {
                    resolve: 'Yes',
                    reject: 'Cancel',
                  },
                },
              }}
            >
              <i className="sv sv-trash2" />
            </ConfirmationButton>
          </QueryFilters.Control>
          <QueryFilters.Control>
            <Button variant="outline-light" onClick={toggleModal('manifestSettings', true)}>
              <i className="sv sv-cog fs-4" />
            </Button>
          </QueryFilters.Control>
        </div>
      </QueryFilters>
      <ManifestSettingsModal show={manifestSettings?.show} onHide={toggleModal('manifestSettings', false)} />
    </>
  );
};

export default ManifestFilters;
